// Wrap any route that needs an authenticated user.
//
//   <Route path="/image-studio" element={<ProtectedRoute><ImageStudio /></ProtectedRoute>} />
//
// In Cognito mode: redirects to the Hosted UI if not signed in.
// In dev mode: always renders children (we're "signed in" as the dev user).
//
// Pass `adminOnly` to additionally gate on the Cognito `admins` group claim.
// Non-admin users get a friendly access-denied page rather than a redirect
// (they're already authenticated, so a re-login would just loop them back).

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Spinner, Alert, SpaceBetween } from '@cloudscape-design/components';
import { useAuth } from './AuthProvider.jsx';

export default function ProtectedRoute({ children, adminOnly = false, internalOnly = false }) {
  const { isLoading, isAuthenticated, isAdmin, isInternal, signIn, error } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      signIn();
    }
  }, [isLoading, isAuthenticated, error, signIn]);

  if (error) {
    return (
      <Box padding="xxl" textAlign="center">
        <h2>Authentication error</h2>
        <p>{error.message}</p>
        <button onClick={signIn}>Try again</button>
      </Box>
    );
  }

  if (isLoading || !isAuthenticated) {
    return (
      <Box padding="xxl" textAlign="center">
        <Spinner size="large" />
        <Box variant="p" margin={{ top: 'm' }}>Signing you in…</Box>
      </Box>
    );
  }

  if (adminOnly && !isAdmin) {
    return (
      <Box padding="xxl" textAlign="center">
        <SpaceBetween size="m" alignItems="center">
          <Alert type="warning" header="Access denied">
            This page is restricted to admins. If you believe you should have
            access, ask your toolkit administrator to add you to the
            <code> admins </code> group.
          </Alert>
          <Link to="/">← Back to SmartBiz Toolkit</Link>
        </SpaceBetween>
      </Box>
    );
  }

  if (internalOnly && !isInternal) {
    return (
      <Box padding="xxl" textAlign="center">
        <SpaceBetween size="m" alignItems="center">
          <Alert type="warning" header="Access denied">
            This page is restricted to Amazon-internal users. If you believe
            you should have access, ask your toolkit administrator to add you
            to the <code> amazon-internal </code> group.
          </Alert>
          <Link to="/">← Back to SmartBiz Toolkit</Link>
        </SpaceBetween>
      </Box>
    );
  }

  return children;
}
